<template>
  <div class="bg-surface-primary">
    <AppAccordion
      type="link"
      :is-open="appliedDiscountsWithCode.length > 0"
      :only-mobile="false"
      :base-html-id="accordionId"
    >
      <template #header>
        <div class="px-16">
          <p
            class="heading-xxs text-text-link underline hover:text-text-link-hover"
          >
            Lägg till rabattkod
          </p>
        </div>
      </template>
      <div
        class="pb-16"
        :class="{ invisible: showLoading, 'h-0': showLoading }"
      >
        <form
          novalidate
          class="px-16 flex flex-col md:flex-row md:items-center w-full max-w-[550px] xl:w-5/6"
          @submit.prevent="checkCode"
        >
          <InputText
            class="me-0 md:me-8 basis-1/2"
            name="rabattkod"
            type="text"
            label="Fyll i din rabattkod"
            validate="discountCodeNotEmpty"
            placeholder="Fyll i din rabattkod här"
            :auto-show-error-msg="false"
          />
          <AppButton color="primary" full-width class="basis-2/4">
            Kontrollera kod
          </AppButton>
        </form>
        <p
          v-if="errors.rabattkod"
          data-testid="cupon-code-error-message"
          class="text-text-error px-32 py-4"
        >
          {{ errors.rabattkod }}
        </p>
        <CheckoutAppliedDiscountCodesChips
          @on-remove-discount-code="removeDiscountCode"
        />
      </div>
      <div
        v-if="!showLoading && checkoutStore.cartContainsBundle"
        class="px-16 pb-16"
      >
        <!-- <AppNotification type="info" class="w-full lg:w-3/4">
          <h6>Har du en värvakod?</h6>
          <p>
            Värvakoden lägger du till i nästa steg efter att du valt ditt
            nummer.
          </p>
        </AppNotification> -->
      </div>
      <div v-if="showLoading" class="px-16 pb-16 w-full lg:w-3/4">
        <RoundedLoadingBox :message="loadingMessage" />
      </div>
    </AppAccordion>
  </div>
</template>

<script lang="ts" setup>
import type { HalebopRequestErrorData } from '~/repository/factory';
import { general_clearCouponAffectedCaches } from '~/utils/general/clearCouponAffectedCaches';

const { $api } = useNuxtApp();
const { handleSubmit, isSubmitting, errors } = useForm();
const checkoutStore = useMyCheckoutStore();
const notificationStore = useNotificationStore();
const accordionId = useId();
const isRemovingADiscountCode = ref(false);

const showLoading = computed(
  () => isSubmitting.value || isRemovingADiscountCode.value,
);
const loadingMessage = computed(() =>
  isSubmitting.value
    ? 'Kontrollerar kod'
    : isRemovingADiscountCode.value
      ? 'Tar bort rabattkod'
      : 'Kontrollerar kod',
);

type DiscountChipViewModel = {
  couponCode: string;
};
const appliedDiscountsWithCode: DiscountChipViewModel[] =
  checkoutStore.fetchedData.cart?.appliedDiscountsCouponCodes
    ?.filter((discount) => !!discount)
    ?.map((discount) => ({
      couponCode: discount,
    })) ?? [];

const { pushCheckoutOptionsEvent } = useGoogleAnalyticsEvents();

const checkCode = handleSubmit(async (values, { setFieldError }) => {
  try {
    await $api.ShoppingCartModule.ApplyDiscountCoupon(values.rabattkod);
    checkoutStore.refreshCartAsyncDataHook();
    general_clearCouponAffectedCaches();
    pushCheckoutOptionsEvent(values.rabattkod);
  } catch (e) {
    let msg =
      'Kunde inte applicera rabattkoden, vänligen kontrollera att den är korrekt.';

    if (isNuxtError<HalebopRequestErrorData>(e) && e.data?.detail) {
      msg = e.data.detail;
    }

    setFieldError('rabattkod', msg);
  }
});

const removeDiscountCode = async (discountCode: string) => {
  isRemovingADiscountCode.value = true;
  try {
    await $api.CustomerModule.RemoveDiscountCouponCode(discountCode);
    checkoutStore.refreshCartAsyncDataHook();
    general_clearCouponAffectedCaches();
  } catch (e) {
    notificationStore.setNewNotification({
      type: 'error',
      title: 'Ett fel uppstod',
      msg: 'Det gick inte att ta bort rabattkoden just nu.',
      isCloseable: true,
      link: {
        url: 'https://www.halebop.se/support',
        text: 'Kontakta supporten',
      },
    });
  } finally {
    isRemovingADiscountCode.value = false;
  }
};
</script>

<style scoped lang="scss"></style>
